<div class="page-head">
  <div>
    <h1 class="title">Dashboard</h1>
    <p class="desc">Snapshot of your agency, providers, applications and booking activity.</p>
  </div>
  <div class="flex flex-wrap gap-2">
    <a class="btn btn-outline" [href]="docsUrl" target="_blank" rel="noopener">
      API docs
      <app-icon name="arrow-up-right" />
    </a>
    <a class="btn btn-primary" [href]="bpUrl" target="_blank" rel="noopener">
      <app-icon name="monitor" />
      Open BookingPad
    </a>
  </div>
</div>

<!-- KPIs -->
<div class="kpis">
  <div class="card p-5">
    <div class="flex items-center justify-between">
      <span class="kpi-l">Active providers</span>
      <app-icon name="plane" class="text-primary" />
    </div>
    @if (loaded) {
      <div class="kpi-v">{{ activeProviders }}</div>
      <div class="kpi-s">of {{ providers.length }} configured</div>
    } @else {
      <div class="skeleton mt-3 h-7 w-16"></div>
    }
  </div>
  <div class="card p-5">
    <div class="flex items-center justify-between">
      <span class="kpi-l">Enabled applications</span>
      <app-icon name="apps" class="text-primary" />
    </div>
    @if (!isConsumerOwner) {
      <div class="kpi-v">—</div>
      <div class="kpi-s">Managed by your main agency</div>
    } @else if (loaded) {
      <div class="kpi-v">{{ enabledApps }}</div>
      <div class="kpi-s">{{ availableApps }} more available</div>
    } @else {
      <div class="skeleton mt-3 h-7 w-16"></div>
    }
  </div>
  <div class="card p-5">
    <div class="flex items-center justify-between">
      <span class="kpi-l">Bookings issued (30d)</span>
      <app-icon name="ticket" class="text-primary" />
    </div>
    @if (loaded) {
      <div class="kpi-v">{{ issuedTotal | number }}</div>
      <div class="kpi-s">of {{ bookedTotal | number }} booked</div>
    } @else {
      <div class="skeleton mt-3 h-7 w-16"></div>
    }
  </div>
  @if (isSuperagency) {
    <div class="card p-5">
      <div class="flex items-center justify-between">
        <span class="kpi-l">Agents in use</span>
        <app-icon name="users" class="text-primary" />
      </div>
      @if (loaded) {
        <div class="kpi-v">
          {{ agentsUsed }}
          <span class="text-base font-medium text-muted-foreground">/ {{ agentLimitLabel }}</span>
        </div>
        <div class="kpi-s">Plan allowance</div>
      } @else {
        <div class="skeleton mt-3 h-7 w-16"></div>
      }
    </div>
  } @else {
    <div class="card p-5">
      <div class="flex items-center justify-between">
        <span class="kpi-l">Agency managers</span>
        <app-icon name="users" class="text-primary" />
      </div>
      @if (loaded) {
        <div class="kpi-v">{{ managerCount }}</div>
        <div class="kpi-s">Active manager accounts</div>
      } @else {
        <div class="skeleton mt-3 h-7 w-16"></div>
      }
    </div>
  }
</div>

<div class="two-one mt-6">
  <!-- Bookings chart -->
  <div class="card">
    <div class="card-h">
      <h2 class="card-t">Bookings — last 30 days</h2>
      <div class="flex flex-wrap items-center gap-4">
        <span class="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span class="dot bg-primary"></span>
          Created
        </span>
        <span class="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span class="dot bg-foreground"></span>
          Issued
        </span>
        <a class="btn btn-ghost btn-sm" routerLink="/reports">
          View reports
          <app-icon name="arrow-up-right" class="sm" />
        </a>
      </div>
    </div>
    <div class="card-c">
      <div class="relative h-72 w-full">
        @if (loaded) {
          <canvas
            baseChart
            type="line"
            [data]="chartData"
            [options]="chartOptions"
            [plugins]="chartPlugins"
            aria-label="Bookings created and issued per day, last 30 days"
          ></canvas>
        } @else {
          <div class="skeleton h-full w-full"></div>
        }
      </div>
    </div>
  </div>

  <!-- Agency profile -->
  <div class="card">
    <div class="card-h">
      <h2 class="card-t">Agency profile</h2>
      <a class="btn btn-ghost btn-sm" routerLink="/settings">Edit</a>
    </div>
    <div class="card-c">
      <div class="mb-5 flex items-center gap-3">
        <div class="chip h-11 w-11 font-bold">{{ agencyInitials || '—' }}</div>
        <div class="flex min-w-0 flex-col">
          <span class="truncate font-medium">{{ agencyName || '—' }}</span>
        </div>
      </div>
      <dl class="dl">
        <dt>IATA number</dt>
        <dd>{{ consumer?.iata_number || '—' }}</dd>
        <dt>City code</dt>
        <dd>{{ consumer?.city_code || '—' }}</dd>
        <dt>Currency</dt>
        <dd>{{ consumer?.currency_code || '—' }}</dd>
      </dl>
      <hr class="sep mt-5 mb-4" />
      <div class="mb-2.5 flex items-center justify-between">
        <span class="text-sm text-muted-foreground">Agency managers</span>
        <span class="badge badge-secondary">{{ managerCount }}</span>
      </div>
      <div class="flex flex-col gap-2">
        @for (agent of visibleAgents; track agent.id) {
          <div class="flex min-w-0 items-center gap-2 text-sm">
            <span class="avatar">{{ agent.email.charAt(0) }}</span>
            <span class="truncate">{{ agent.email }}</span>
          </div>
        } @empty {
          @if (loaded) {
            <span class="text-sm text-muted-foreground">—</span>
          } @else {
            <div class="skeleton h-4 w-3/4"></div>
            <div class="skeleton h-4 w-2/3"></div>
          }
        }
        @if (agentAccounts.length > 3) {
          <button
            type="button"
            class="btn btn-ghost btn-sm self-start pl-0"
            (click)="toggleShowAll()"
          >
            {{ showAllAgents ? 'Show less' : 'Show ' + (agentAccounts.length - 3) + ' more' }}
            <app-icon [name]="showAllAgents ? 'chevron-up' : 'chevron-down'" class="sm" />
          </button>
        }
      </div>
    </div>
  </div>
</div>

<div class="split mt-6">
  <!-- Plan allowance (superagency only) -->
  @if (isSuperagency) {
    <div class="card">
      <div class="card-h">
        <div class="flex items-center gap-2">
          <h2 class="card-t">Plan allowance</h2>
          <span
            class="text-muted-foreground"
            title="This table indicates the current agents and subagencies already in use, and the total number of agents and subagencies that you have included in your subscription."
          >
            <app-icon name="info" label="About plan allowance" />
          </span>
        </div>
        <span class="badge badge-outline">Superagency</span>
      </div>
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1 px-6 pt-2 text-sm">
        <span class="text-muted-foreground">Subscription plan</span>
        <span class="font-semibold">{{ consumer?.subscription?.name || '—' }}</span>
        <!-- Not available yet: rendered disabled until subscription changes are supported. -->
        <button
          type="button"
          class="cursor-not-allowed border-0 bg-transparent p-0 text-sm font-medium text-primary opacity-50"
          disabled
          title="Coming soon"
        >
          Change subscription
        </button>
      </div>
      <div class="card-c flex flex-col gap-5">
        <div>
          <div class="mb-2 flex items-center justify-between text-sm">
            <span class="flex items-center gap-2">
              <app-icon name="users" class="text-muted-foreground" />
              Agents
            </span>
            <span class="font-medium tabular-nums">{{ agentsUsed }} / {{ agentLimitLabel }}</span>
          </div>
          <div class="progress" [class.bg-muted]="!agentLimit">
            <span [style.width.%]="agentUsagePct"></span>
          </div>
        </div>
        <div>
          <div class="mb-2 flex items-center justify-between text-sm">
            <span class="flex items-center gap-2">
              <app-icon name="building" class="text-muted-foreground" />
              Subagencies
            </span>
            <span class="font-medium tabular-nums">
              {{ subagenciesUsed }} /
              @if (subagencyUnlimited) {
                <span class="font-normal text-muted-foreground">Unlimited</span>
              } @else {
                {{ subagencyLimit }}
              }
            </span>
          </div>
          <div class="progress" [class.bg-muted]="!subagencyLimit">
            <span [style.width.%]="subagencyUsagePct"></span>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Providers -->
  <div class="card">
    <div class="card-h">
      <div class="flex items-center gap-2">
        <h2 class="card-t">Providers enabled</h2>
        <span class="badge badge-secondary">{{ activeProviders }}</span>
      </div>
      <a class="btn btn-ghost btn-sm" routerLink="/providers">
        Manage
        <app-icon name="arrow-up-right" class="sm" />
      </a>
    </div>
    <div class="card-c flex flex-wrap gap-2">
      @for (item of providers; track item.provider.id) {
        <span
          class="badge badge-outline"
          [class.text-muted-foreground]="!isLive(item)"
          [title]="item.provider.name + (isLive(item) ? '' : ' — inactive')"
        >
          <span class="dot" [class.dot-on]="isLive(item)"></span>
          {{ item.provider.agw_id }}
        </span>
      } @empty {
        @if (loaded) {
          <span class="text-sm text-muted-foreground">No providers configured.</span>
        } @else {
          <div class="skeleton h-6 w-full"></div>
        }
      }
    </div>
  </div>
</div>
